import { type PropsWithChildren } from 'react';
import type { DSC } from '@mentimeter/ragnar-dsc';
import { alpha } from '@mentimeter/ragnar-colors';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { Layout } from '@mentimeter/http-clients';

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <Box flex="1 1 auto" width="100%" height="100%">
    {children}
  </Box>
);

const FullSizeBackground = ({
  backgroundImage,
  backgroundImageSize,
  shouldShowColorOverlay,
  enableAnimations,
  altText,
  ...props
}: BoxT & {
  backgroundImage: string;
  backgroundImageSize: string | 'cover' | 'contain';
  enableAnimations?: boolean | undefined;
  shouldShowColorOverlay?: boolean | undefined;
  altText: string;
}) => {
  return (
    <Box
      role="img"
      aria-label={altText}
      width="100%"
      height="100%"
      data-testid="content-image-full-size"
      extend={({ theme }) => ({
        backgroundImage: shouldShowColorOverlay
          ? `linear-gradient(
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)},
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)}
              ), url(${backgroundImage})`
          : `url(${backgroundImage})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: backgroundImageSize || 'cover',
      })}
      {...props}
    />
  );
};

const extend =
  (contentImage: string) =>
  ({ theme }: { theme: DSC }) => ({
    backgroundColor: contentImage
      ? 'transparent'
      : alpha(theme.colors.neutralWeak, 0.5),
    backgroundImage: `url(${contentImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  });

export const SfinxBackground = ({
  children,
  imageUrl,
  layoutType,
  layoutSize,
  overrideToFullBackgroundImageWithSize,
  altText = '',
}: PropsWithChildren<{
  imageUrl: string;
  layoutType: Layout['type'] | undefined;
  layoutSize: Layout['size'] | undefined;
  overrideToFullBackgroundImageWithSize: false | 'cover' | 'contain';
  altText: string | undefined;
}>) => {
  if (overrideToFullBackgroundImageWithSize !== false) {
    return (
      <FullSizeBackground
        altText={altText}
        backgroundImage={imageUrl}
        backgroundImageSize={overrideToFullBackgroundImageWithSize}
      >
        {children}
      </FullSizeBackground>
    );
  }

  if (layoutType === 'full-bg') {
    return (
      <FullSizeBackground
        altText={altText}
        backgroundImage={imageUrl}
        backgroundImageSize="cover"
        shouldShowColorOverlay
      >
        {children}
      </FullSizeBackground>
    );
  }

  const isVerticalImage = ['left', 'right'].includes(layoutType || '');
  const isHorizontalImage = ['top', 'bottom'].includes(layoutType || '');

  if (isHorizontalImage) {
    return (
      <Box
        width="100%"
        height="100%"
        flexDirection={layoutType === 'top' ? 'column' : 'column-reverse'}
      >
        <Box
          role="img"
          aria-label={altText}
          width="100%"
          height={layoutSize}
          extend={extend(imageUrl)}
          data-testid="content-image-horizontal"
        />
        {/* Children needs to be rendered after the image in order to be able to be displayed
        on top of it, using zindex. (needed for open ended screenshots) */}
        <Wrap>{children}</Wrap>
      </Box>
    );
  }

  if (isVerticalImage) {
    return (
      <Box
        width="100%"
        height="100%"
        flexDirection={layoutType === 'right' ? 'row-reverse' : 'row'}
      >
        <Box
          role="img"
          aria-label={altText}
          width={layoutSize}
          height="100%"
          data-testid="content-image-vertical"
          extend={extend(imageUrl)}
        />
        {/* Children needs to be rendered after the image in order to be able to be displayed
        on top of it, using zindex. (needed for open ended screenshots) */}
        <Wrap>{children}</Wrap>
      </Box>
    );
  }

  return children;
};
